// Flat dashboard payloads (GET /dashboard/{waste-outlet|recycler|trucker}/{org}[/overview]),
// the pilot projection and the operations list — all computed from the story's pickup
// orders so every figure reconciles with the per-material split and branch metrics.

import {
  DEMO_DRIVERS,
  DEMO_OUTLET_NAME,
  DEMO_OUTLET_ORG_ID,
  DEMO_OUTLET_STAFF,
  DEMO_RECYCLER_NAME,
  DEMO_RECYCLER_ORG_ID,
  DEMO_CONTAINERS,
  DEMO_SITES,
  isoDate,
  paginate,
  rngFor,
  round,
  type DemoPickupOrder,
} from '../core';
import type {
  DailyWasteAmount,
  DashboardStatistics,
  PilotDashboardStatistics,
  WasteTypeWithMaterials,
} from '../../types/dashboard.type';
import type {
  WasteOutletOpsCollectionSiteSummary,
  WasteOutletOpsListResponse,
} from '../../types/wasteOutletOps.type';
import { containerCapacityKg, fillReportsForSite, outletContainers } from './containers';
import { activeTruckLocations, todaysRuns } from './fleet';
import { materialCategory, materialName, pct, shiftMonth, sum, toMs } from './helpers';
import { orderKg, ordersForOrg, ordersInMonth } from './pickupOrders';
import { collectionSitesFor } from './sites';

const PERIOD_KEYS = [
  'time_from_full_to_collection',
  'time_from_collection_to_delivery',
  'full_percentage',
  'recycler_appetite',
  'truck_driver_count',
  'supply_per_recycler',
  'sustainable_supply',
  'collection_frequency',
  'average_container_fill_rate',
  'waste_diversion_rate',
  'average_incentive_per_kg',
  'trips_today_count',
  'incoming_waste_breakdown',
  'waste_type_material_breakdown',
  'total_revenue',
  'total_revenue_last_month',
  'average_processing_time',
  'quality_compliance_rate',
  'rejected_waste_percentage',
  'processing_capacity_utilization',
  'average_route_efficiency',
  'fuel_consumption_per_trip',
  'maintenance_schedule_compliance',
  'driver_performance_score',
  'customer_satisfaction_score',
  'waste_compaction_efficiency',
  'storage_capacity_utilization',
  'emergency_collection_requests',
  'waste_quality_rating',
  'total_orders',
  'pending_orders',
  'in_transit_orders',
  'completed_orders',
  'cancelled_orders',
  'completion_rate',
  'cancellation_rate',
  'total_waste_amount',
  'completed_waste_amount',
  'average_order_amount',
  'orders_today',
  'average_processing_time_hours',
] as const;

type PeriodKey = (typeof PERIOD_KEYS)[number];
type PeriodFigures = Pick<DashboardStatistics, PeriodKey>;
type PreviousFigures = { [K in PeriodKey as `previous_month_${K}`]: DashboardStatistics[K] };
type SnapshotFigures = Pick<
  DashboardStatistics,
  | 'active_trucks'
  | 'total_containers'
  | 'active_containers'
  | 'full_containers'
  | 'containers_needing_attention'
  | 'capacity_utilization_rate'
  | 'containers'
>;

const HOUR = 3_600_000;

/** Completed orders grouped by catalog category, with the material names inside. */
export function materialBreakdown(orders: readonly DemoPickupOrder[]): WasteTypeWithMaterials[] {
  const byCategory = new Map<string, Map<string, number>>();
  for (const o of orders) {
    if (o.status !== 'completed') continue;
    const category = materialCategory(o.material_id);
    const materials = byCategory.get(category) ?? new Map<string, number>();
    materials.set(materialName(o.material_id), (materials.get(materialName(o.material_id)) ?? 0) + orderKg(o));
    byCategory.set(category, materials);
  }
  return [...byCategory.entries()]
    .map(([waste_type, materials]) => ({
      waste_type,
      total_kg: round(sum([...materials.values()], (v) => v), 1),
      materials: [...materials.entries()]
        .map(([material_type, amount]) => ({ material_type, amount: round(amount, 1) }))
        .sort((a, b) => b.amount - a.amount),
    }))
    .sort((a, b) => b.total_kg - a.total_kg);
}

/** Requested kg per UTC creation day (every status) — the daily chart's series. */
function dailyRequests(orders: readonly DemoPickupOrder[]): DailyWasteAmount[] {
  const byDay = new Map<string, number>();
  for (const o of orders) {
    const day = o.created_at.slice(0, 10);
    byDay.set(day, (byDay.get(day) ?? 0) + orderKg(o));
  }
  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, amount]) => ({ date, waste_type: 'all', amount: round(amount, 2) }));
}

function revenueOf(orders: readonly DemoPickupOrder[]): number {
  return round(sum(orders, (o) => (o.status === 'completed' ? o.sale_amount_sar ?? 0 : 0)), 2);
}

function hoursBetween(a: string | null, b: string | null): number | null {
  const x = toMs(a);
  const y = toMs(b);
  return x !== null && y !== null ? (y - x) / HOUR : null;
}

function avg(values: number[]): number {
  return values.length ? sum(values, (v) => v) / values.length : 0;
}

function periodFigures(orgId: string, key: string | null): PeriodFigures {
  const orders = ordersInMonth(orgId, key);
  const isRecycler = orgId === DEMO_RECYCLER_ORG_ID;
  const rng = rngFor(`zero-dashboard:${orgId}:${key ?? 'lifetime'}`);
  const completed = orders.filter((o) => o.status === 'completed');
  const count = (status: string) => orders.filter((o) => o.status === status).length;
  const totalKg = round(sum(orders, orderKg), 1);
  const completedKg = round(sum(completed, orderKg), 1);
  const approved = completed.filter((o) => o.approval_status === 'approved');
  const rejected = completed.filter((o) => o.approval_status === 'rejected');
  const decided = approved.length + rejected.length;
  const revenue = revenueOf(orders);
  const approvedKg = sum(approved, orderKg);
  const today = isoDate(new Date());
  const processing = completed.map((o) => hoursBetween(o.created_at, o.completed_at)).filter((h): h is number => h !== null);
  const waitToCollect = completed.map((o) => hoursBetween(o.created_at, o.scheduled_at)).filter((h): h is number => h !== null);
  const containerById = new Map(DEMO_CONTAINERS.map((c) => [c.id, c]));
  const fillAtRequest = orders.map((o) => {
    const c = containerById.get(o.container_id);
    return c ? (containerCapacityKg(c) * o.fill_level) / 100 : 0;
  });
  const weeks = key ? 4.35 : 52;
  const previousKey = key ? shiftMonth(key, -1) : shiftMonth(new Date().toISOString().slice(0, 7), -1);
  const runsToday = todaysRuns().length;
  const tripsToday = isRecycler
    ? runsToday
    : ordersForOrg(orgId).filter(
        (o) => (o.status === 'in_transit' && o.scheduled_at.startsWith(today)) || (o.completed_at ?? '').startsWith(today),
      ).length;
  const containers = isRecycler ? [] : outletContainers();
  const fullShare = containers.length
    ? pct(containers.filter((c) => c.capacity && (c.fill_level ?? 0) / c.capacity >= 0.8).length, containers.length)
    : 0;
  const total = orders.length;

  return {
    time_from_full_to_collection: round(avg(waitToCollect), 1),
    time_from_collection_to_delivery: rng.float(1.4, 3.2, 1),
    full_percentage: key && key !== new Date().toISOString().slice(0, 7) ? rng.float(14, 34, 1) : fullShare,
    recycler_appetite: totalKg,
    truck_driver_count: isRecycler
      ? DEMO_DRIVERS.filter((d) => d.status === 'active').length
      : new Set(orders.map((o) => o.driver_id).filter(Boolean)).size,
    supply_per_recycler: [{ recycler_name: isRecycler ? DEMO_OUTLET_NAME : DEMO_RECYCLER_NAME, supply_amount: completedKg }],
    sustainable_supply: completedKg,
    collection_frequency: round(completed.length / weeks, 2),
    average_container_fill_rate: round(avg(fillAtRequest), 1),
    waste_diversion_rate: pct(completedKg, totalKg),
    average_incentive_per_kg: approvedKg > 0 ? round(revenue / approvedKg, 2) : 0,
    trips_today_count: tripsToday,
    incoming_waste_breakdown: dailyRequests(orders),
    waste_type_material_breakdown: materialBreakdown(orders),
    total_revenue: isRecycler ? 0 : revenue,
    total_revenue_last_month: isRecycler ? 0 : revenueOf(ordersInMonth(orgId, previousKey)),
    average_processing_time: round(avg(processing), 1),
    quality_compliance_rate: decided ? pct(approved.length, decided) : 100,
    rejected_waste_percentage: completedKg > 0 ? pct(sum(rejected, orderKg), completedKg) : 0,
    processing_capacity_utilization: isRecycler ? rng.float(58, 79, 1) : 0,
    average_route_efficiency: rng.float(82, 93, 1),
    fuel_consumption_per_trip: rng.float(11, 17, 1),
    maintenance_schedule_compliance: rng.float(88, 98, 1),
    driver_performance_score: rng.float(84, 95, 1),
    customer_satisfaction_score: rng.float(4.3, 4.9, 1),
    waste_compaction_efficiency: rng.float(62, 78, 1),
    storage_capacity_utilization: rng.float(41, 67, 1),
    emergency_collection_requests: rng.int(0, 3),
    waste_quality_rating: rng.float(4.1, 4.8, 1),
    total_orders: total,
    pending_orders: count('pending'),
    in_transit_orders: count('in_transit'),
    completed_orders: completed.length,
    cancelled_orders: count('cancelled'),
    completion_rate: pct(completed.length, total),
    cancellation_rate: pct(count('cancelled'), total),
    total_waste_amount: totalKg,
    completed_waste_amount: completedKg,
    average_order_amount: total ? round(totalKg / total, 1) : 0,
    orders_today: orders.filter((o) => o.created_at.startsWith(today)).length,
    average_processing_time_hours: round(avg(processing), 1),
  };
}

function previousFigures(f: PeriodFigures): PreviousFigures {
  const out: Record<string, unknown> = {};
  for (const k of PERIOD_KEYS) out[`previous_month_${k}`] = f[k];
  return out as PreviousFigures;
}

function snapshotFigures(orgId: string): SnapshotFigures {
  if (orgId !== DEMO_OUTLET_ORG_ID) {
    return {
      active_trucks: orgId === DEMO_RECYCLER_ORG_ID ? activeTruckLocations(orgId) : [],
      total_containers: 0,
      active_containers: 0,
      full_containers: 0,
      containers_needing_attention: 0,
      capacity_utilization_rate: 0,
      containers: [],
    };
  }
  const containers = outletContainers();
  const fill = sum(containers, (c) => c.fill_level ?? 0);
  const capacity = sum(containers, (c) => c.capacity ?? 0);
  return {
    active_trucks: [],
    total_containers: containers.length,
    active_containers: containers.filter((c) => c.status !== 'maintenance' && c.status !== 'inactive').length,
    full_containers: containers.filter((c) => c.alarms.full === true).length,
    containers_needing_attention: containers.filter(
      (c) => Object.values(c.alarms).some((a) => a === true) || c.status === 'ready_for_pickup' || c.status === 'awaiting_pickup',
    ).length,
    capacity_utilization_rate: pct(fill, capacity),
    containers,
  };
}

/** Month dashboard (`key` = 'YYYY-MM') or lifetime overview (`key` = null). */
export function dashboardStatistics(orgId: string, key: string | null): DashboardStatistics {
  const current = periodFigures(orgId, key);
  const previousKey = key ? shiftMonth(key, -1) : shiftMonth(new Date().toISOString().slice(0, 7), -1);
  const previous = previousFigures(periodFigures(orgId, previousKey));
  return { ...current, ...previous, ...snapshotFigures(orgId) };
}

export function pilotStatistics(orgId: string, key: string): PilotDashboardStatistics {
  const f = periodFigures(orgId, key);
  return {
    waste_diversion_rate: f.waste_diversion_rate,
    total_revenue: f.total_revenue,
    total_orders: f.total_orders,
    completion_rate: f.completion_rate,
  };
}

// ── GET /dashboard/operations/waste-outlets ────────────────────────────────

export function wasteOutletOpsList(query: Record<string, unknown>): WasteOutletOpsListResponse {
  const from = toMs(String(query.start_date ?? '')) ?? 0;
  const to = toMs(String(query.end_date ?? '')) ?? Date.now();
  const inRange = ordersForOrg(DEMO_OUTLET_ORG_ID).filter((o) => {
    const t = Date.parse(o.created_at);
    return t >= from && t < to;
  });
  const sites = collectionSitesFor(DEMO_OUTLET_ORG_ID);
  const collection_sites: WasteOutletOpsCollectionSiteSummary[] = DEMO_SITES.map((site) => {
    const atSite = inRange.filter((o) => o.site_id === site.id);
    const total = sum(atSite, orderKg);
    const done = sum(atSite.filter((o) => o.status === 'completed'), orderKg);
    const meta = sites.find((s) => s.id === site.id);
    return {
      collection_site_id: site.id,
      name: site.name_en,
      latitude: site.lat,
      longitude: site.lng,
      organization_id: DEMO_OUTLET_ORG_ID,
      branch_contact: (meta?.data.contact_info as { phone?: string } | undefined)?.phone ?? site.phone,
      total_pickup_orders: atSite.length,
      total_waste_kg: round(total, 1),
      total_revenue: revenueOf(atSite),
      completed_waste_kg: round(done, 1),
      waste_diversion_rate_percent: pct(done, total),
      employee_count: DEMO_OUTLET_STAFF.filter((s) => s.site_ids.includes(site.id)).length,
      container_count: DEMO_CONTAINERS.filter((c) => c.site_id === site.id).length,
      fill_level_report_count: fillReportsForSite(site.id, from, to),
    };
  });
  const rows = [
    {
      organization_id: DEMO_OUTLET_ORG_ID,
      organization_name: DEMO_OUTLET_NAME,
      total_revenue: revenueOf(inRange),
      total_pickup_orders: inRange.length,
      total_waste_kg: round(sum(inRange, orderKg), 1),
      collection_sites,
      unassigned_pickup_orders: 0,
    },
  ];
  const offset = Math.max(0, Number(query.offset ?? 0) || 0);
  const limit = Math.max(1, Number(query.limit ?? 50) || 50);
  const page = paginate(rows.slice(offset), 1, limit);
  return { total_count: rows.length, result_list: page.items };
}
